// state.js - 글로벌 상태 관리
// Extracted from app.js for modular architecture

/**
 * @typedef {'pairing' | 'camera' | 'preview' | 'uploading' | 'complete' | 'settings' | 'error' | 'cornerEdit' | 'processOptions' | 'multipage'} ScreenName
 * @typedef {'simple' | 'document'} ScanMode
 */

/**
 * 앱의 글로벌 상태
 * 각 모듈에서 import하여 사용
 */
export const state = {
  /** @type {ScreenName | null} */
  currentScreen: null,

  /** @type {ScanMode} */
  scanMode: 'simple',

  /** @type {string[]} 스캔된 페이지 dataURL 배열 */
  scannedPages: [],

  /** @type {string | null} 촬영된 이미지 (사진 모드) */
  capturedImage: null,

  /** @type {string | null} 코너 편집 중인 원본 이미지 */
  currentRawCapture: null,

  /** @type {string | null} 원근보정 후 처리 옵션 선택 전 이미지 */
  correctedImageBeforeProcess: null,

  /** @type {{ bw: { dataURL: string, estimatedSize: number }, gray: { dataURL: string, estimatedSize: number }, color: { dataURL: string, estimatedSize: number } } | null} */
  processedVariants: null,

  /** @type {'bw' | 'gray' | 'color'} 선택된 필터 */
  selectedFilter: 'bw',

  /** @type {boolean} 이 탭이 활성 탭인지 */
  isActiveTab: true,

  /** @type {any} 실시간 디텍터 인스턴스 */
  realtimeDetector: null,

  /** @type {{ plan: 'free' | 'pro', dailyUploadsUsed: number, dailyUploadLimit: number }} */
  subscription: {
    plan: 'free',
    dailyUploadsUsed: 0,
    dailyUploadLimit: 3,
  }
};

/** 최대 페이지 수 */
export const MAX_PAGES = 20;

/** 촬영 사진 임시 저장 키 */
export const PENDING_CAPTURE_KEY = 'scantodesk_pending_capture';

/**
 * 상태 값 가져오기
 * @template {keyof typeof state} K
 * @param {K} key
 * @returns {typeof state[K]}
 */
export function getState(key) {
  return state[key];
}

/**
 * 상태 값 설정
 * @template {keyof typeof state} K
 * @param {K} key
 * @param {typeof state[K]} value
 */
export function setState(key, value) {
  state[key] = value;
}

/**
 * 스캔 관련 상태 초기화
 * 새로운 스캔 시작 시 호출
 */
export function resetScanState() {
  state.scannedPages = [];
  state.capturedImage = null;
  state.currentRawCapture = null;
  state.correctedImageBeforeProcess = null;
  state.processedVariants = null;
}

/**
 * 페이지 추가
 * @param {string} dataURL
 * @returns {boolean} 추가 성공 여부
 */
export function addPage(dataURL) {
  if (state.scannedPages.length >= MAX_PAGES) {
    return false;
  }
  state.scannedPages.push(dataURL);
  return true;
}

/**
 * 페이지 삭제
 * @param {number} index
 * @returns {boolean} 삭제 성공 여부
 */
export function removePage(index) {
  if (index < 0 || index >= state.scannedPages.length) {
    return false;
  }
  state.scannedPages.splice(index, 1);
  return true;
}

/**
 * 페이지 재정렬
 * @param {number} fromIndex
 * @param {number} toIndex
 * @returns {boolean} 재정렬 성공 여부
 */
export function reorderPages(fromIndex, toIndex) {
  const pages = state.scannedPages;
  if (
    fromIndex < 0 || fromIndex >= pages.length ||
    toIndex < 0 || toIndex >= pages.length
  ) {
    return false;
  }
  const [removed] = pages.splice(fromIndex, 1);
  pages.splice(toIndex, 0, removed);
  return true;
}

/**
 * 모든 페이지 가져오기
 * @returns {string[]}
 */
export function getPages() {
  return [...state.scannedPages];
}

/**
 * 페이지 수 가져오기
 * @returns {number}
 */
export function getPageCount() {
  return state.scannedPages.length;
}

/**
 * 모든 페이지 삭제
 */
export function clearPages() {
  state.scannedPages = [];
}
